// components/ReleaseNotes.jsx
import React, { useState } from 'react';
import ReleaseInput from './ReleaseInput';
import ReleasePreview from './ReleasePreview';

const initialReleaseData = {
  version: '',
  releaseTitle: '',
  date: '',
  description: '',
  sections: [
    { title: 'Features', items: [] },
    { title: 'Bug Fixes', items: [] },
    { title: 'Maintenance', items: [] },
  ],
};

const ReleaseNotes = () => {
  const [releaseData, setReleaseData] = useState(initialReleaseData);
  const [activeComponent, setActiveComponent] = useState('input'); // State for tracking active component on mobile view

  // Function to copy releaseData as JSON to clipboard
  const copyToClipboard = () => {
    const formattedData = formatReleaseData(releaseData);
    const jsonString = JSON.stringify(formattedData, null, 2);
    navigator.clipboard.writeText(jsonString).then(
      () => {
        alert('Release notes JSON copied to clipboard!');
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  // Function to format releaseData to match the exact JSON format provided
  const formatReleaseData = (releaseData) => {
    // Clone releaseData to avoid mutating state
    const release = { ...releaseData };

    // Format date to match "YYYY-MM-DDTHH:MM:SS±HH:MM"
    const date = new Date(release.date);

    const pad = (num) => num.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    // Get timezone offset in minutes
    const timezoneOffset = -date.getTimezoneOffset(); // in minutes
    const offsetSign = timezoneOffset >= 0 ? '+' : '-';
    const offsetHours = pad(Math.floor(Math.abs(timezoneOffset) / 60));
    const offsetMinutes = pad(Math.abs(timezoneOffset) % 60);

    const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;

    release.date = formattedDate;

    return release;
  };

  return (
    <div className="w-full h-screen">
      {/* Mobile View Toggle Buttons */}
      <div className="sm:hidden flex justify-around bg-gray-100 p-2 pt-16 fixed top-0 left-0 w-full">
        <button
          onClick={() => setActiveComponent('input')}
          className={`w-1/2 p-2 text-sm font-medium ${
            activeComponent === 'input' ? 'bg-blue-500 text-white' : 'bg-white text-blue-500'
          } rounded-l-md`}
        >
          Input
        </button>
        <button
          onClick={() => setActiveComponent('preview')}
          className={`w-1/2 p-2 text-sm font-medium ${
            activeComponent === 'preview' ? 'bg-blue-500 text-white' : 'bg-white text-blue-500'
          } rounded-r-md`}
        >
          Preview
        </button>
      </div>

      {/* Desktop View: Show Both Components Side by Side */}
      <div className="hidden sm:flex w-full h-full">
        <div className="w-2/5 bg-white overflow-y-scroll">
          <ReleaseInput releaseData={releaseData} setReleaseData={setReleaseData} />
        </div>
        <div className="w-3/5 p-8 overflow-y-scroll">
          {/* Copy JSON Button */}
          <button
            onClick={copyToClipboard}
            className="bg-blue-500 text-white px-4 py-2 rounded mb-4"
          >
            Copy JSON
          </button>
          <ReleasePreview releaseData={releaseData} />
        </div>
      </div>

      {/* Mobile View: Show One Component at a Time */}
      <div className="block sm:hidden mt-12">
        {activeComponent === 'input' && (
          <div className="w-full bg-white p-8 overflow-y-scroll">
            <ReleaseInput releaseData={releaseData} setReleaseData={setReleaseData} />
          </div>
        )}
        {activeComponent === 'preview' && (
          <div className="w-full p-8 overflow-y-scroll">
            {/* Copy JSON Button */}
            <button
              onClick={copyToClipboard}
              className="bg-blue-500 text-white px-4 py-2 rounded mb-4"
            >
              Copy JSON
            </button>
            <ReleasePreview releaseData={releaseData} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReleaseNotes;